import * as React from "react";
import { useAction, useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convexApi";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { formatCurrency } from "@/lib/format";

type CancellationOrder = {
  _id?: string;
  id?: string;
  orderNumber?: string;
  status?: string;
  totalAmount?: number;
};

type AdminCancellationDialogProps = {
  order: CancellationOrder | null;
  authUserId?: string;
  onClose: () => void;
};

type RefundChoice = "points" | "original";

type CancellationAnalysis = {
  canCancel: boolean;
  reason?: string | null;
  totalAmount?: number;
  paidAmount?: number;
  requiresChoice: boolean;
  resolutionMode?: string;
  originalPaymentMethod?: string;
  payment?: { status?: string } | null;
  carrier?: {
    name?: string | null;
    code?: string | null;
    trackingNumber?: string | null;
    selectedService?: string | null;
  };
  refundOptions: {
    type: RefundChoice;
    amount: number;
    fee: number;
    netAmount: number;
    pointsEquivalent: number;
    description: string;
  }[];
};

/** Parents acquire the cancellation hold before opening this dialog. */
export function AdminCancellationDialog(props: AdminCancellationDialogProps) {
  if (!props.order) return null;

  return (
    <HeldCancellationDialog
      key={props.order._id ?? props.order.id ?? props.order.orderNumber}
      {...props}
      order={props.order}
    />
  );
}

function HeldCancellationDialog({
  order,
  authUserId,
  onClose,
}: Omit<AdminCancellationDialogProps, "order"> & { order: CancellationOrder }) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [queryAttempt, setQueryAttempt] = React.useState(0);
  const titleRef = React.useRef<HTMLHeadingElement>(null);

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !isSubmitting) onClose(); }}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto overscroll-contain sm:max-w-[680px]"
        showCloseButton={!isSubmitting}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus();
        }}
        onEscapeKeyDown={(event) => { if (isSubmitting) event.preventDefault(); }}
        onInteractOutside={(event) => { if (isSubmitting) event.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle ref={titleRef} tabIndex={-1}>Cancel Order</DialogTitle>
          <DialogDescription>
            This order is on hold for cancellation. It stays on hold if you close this dialog.
            Confirm cancellation below to restore inventory and start any refund.
          </DialogDescription>
        </DialogHeader>
        {order.orderNumber ? <p className="text-sm font-medium">Order {order.orderNumber}</p> : null}
        <RouteErrorBoundary
          key={queryAttempt}
          errorView={
            <div className="space-y-4">
              <p role="alert" className="text-sm text-destructive">
                Unable to load refund options. Try again, or keep the order on hold and reopen cancellation.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" disabled={isSubmitting} onClick={onClose}>Keep on hold</Button>
                <Button type="button" disabled={isSubmitting} onClick={() => setQueryAttempt((attempt) => attempt + 1)}>
                  Try again
                </Button>
              </DialogFooter>
            </div>
          }
        >
          <CancellationForm
            order={order}
            authUserId={authUserId}
            onClose={onClose}
            isSubmitting={isSubmitting}
            onSubmittingChange={setIsSubmitting}
          />
        </RouteErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}

function CancellationForm({
  order,
  authUserId,
  onClose,
  isSubmitting,
  onSubmittingChange,
}: Omit<AdminCancellationDialogProps, "order"> & {
  order: CancellationOrder;
  isSubmitting: boolean;
  onSubmittingChange: (submitting: boolean) => void;
}) {
  const orderId = order._id ?? order.id;
  const identifiers = {
    ...(orderId ? { orderId } : {}),
    ...(order.orderNumber ? { orderNumber: order.orderNumber } : {}),
  };
  const canAnalyze = Boolean(authUserId && (orderId || order.orderNumber));
  const analysis = useQuery(
    api.cancellation.analyzeAdminHeldCancellation,
    canAnalyze ? { authUserId, ...identifiers } : "skip",
  ) as CancellationAnalysis | undefined;
  const cancelOrder = useAction(api.cancellation.adminCancelOrder);
  const [refundChoice, setRefundChoice] = React.useState<RefundChoice | "">("");
  const [carrierConfirmed, setCarrierConfirmed] = React.useState(false);
  const [notes, setNotes] = React.useState("");
  const [validationAttempted, setValidationAttempted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const submittingRef = React.useRef(false);
  const refundRef = React.useRef<HTMLInputElement>(null);
  const carrierRef = React.useRef<HTMLInputElement>(null);
  const formId = React.useId();
  const needsRefundChoice = Boolean(
    analysis?.requiresChoice && !analysis.refundOptions.some((option) => option.type === refundChoice),
  );
  const isLoading = canAnalyze && analysis === undefined;
  const canCancel = canAnalyze && analysis?.canCancel === true;
  const carrierParts = analysis?.carrier
    ? [analysis.carrier.name || analysis.carrier.code, analysis.carrier.selectedService,
        analysis.carrier.trackingNumber ? `Tracking ${analysis.carrier.trackingNumber}` : null].filter(Boolean)
    : [];

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submittingRef.current || !canCancel || !authUserId || !analysis) return;
    setValidationAttempted(true);
    if (needsRefundChoice) {
      refundRef.current?.focus();
      return;
    }
    if (!carrierConfirmed) {
      carrierRef.current?.focus();
      return;
    }

    submittingRef.current = true;
    onSubmittingChange(true);
    setSubmitError(null);
    try {
      const result = await cancelOrder({
        authUserId,
        ...identifiers,
        carrierCancelled: true,
        ...(analysis.requiresChoice && refundChoice ? { refundChoice } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      if (result?.success === false) {
        throw new Error(result.message || "Unable to cancel the order. Try again.");
      }
      toast.success(order.orderNumber ? `Order ${order.orderNumber} cancelled and inventory restored.` : "Order cancelled and inventory restored.");
      onClose();
    } catch (error: unknown) {
      const message = toUserFacingErrorMessage(error, "Unable to cancel the order. Try again.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      submittingRef.current = false;
      onSubmittingChange(false);
    }
  }

  return (
    <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
      <div role="status" className="text-sm text-muted-foreground">
        {isLoading ? <span className="flex items-center gap-2"><Loader2 aria-hidden="true" className="size-4 motion-safe:animate-spin" />Loading refund options...</span> : null}
      </div>
      {!canAnalyze ? (
        <p role="alert" className="text-sm text-destructive">Unable to identify this order or your account. Close this dialog and reload the page.</p>
      ) : analysis?.canCancel === false ? (
        <p role="alert" className="text-sm text-destructive">{analysis.reason || "This order cannot be cancelled from this flow."}</p>
      ) : null}
      {analysis && canCancel ? (
        <>
          <dl className="grid gap-3 rounded-xl border bg-muted/20 p-4 text-sm sm:grid-cols-2">
            {typeof analysis.totalAmount === "number" ? <div><dt className="text-muted-foreground">Order total</dt><dd className="font-medium tabular-nums">{formatCurrency(analysis.totalAmount)}</dd></div> : null}
            {typeof analysis.paidAmount === "number" ? <div><dt className="text-muted-foreground">Paid amount</dt><dd className="font-medium tabular-nums">{formatCurrency(analysis.paidAmount)}</dd></div> : null}
            {analysis.originalPaymentMethod ? <div><dt className="text-muted-foreground">Payment method</dt><dd className="uppercase">{analysis.originalPaymentMethod}</dd></div> : null}
            {analysis.payment?.status ? <div><dt className="text-muted-foreground">Payment status</dt><dd>{analysis.payment.status}</dd></div> : null}
            <div className="sm:col-span-2"><dt className="text-muted-foreground">Carrier</dt><dd>{carrierParts.length ? carrierParts.join(" · ") : "No carrier record found"}</dd></div>
          </dl>
          <fieldset disabled={isSubmitting} className="space-y-3">
            <legend className="mb-2 text-sm font-medium">Refund option</legend>
            {analysis.refundOptions.length > 0 ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {analysis.refundOptions.map((option, index) => (
                  <label key={option.type} className="flex cursor-pointer items-start gap-3 rounded-xl border p-3 has-[:checked]:border-primary has-[:checked]:bg-muted/40">
                    <input
                      ref={index === 0 ? refundRef : undefined}
                      type="radio"
                      name={`${formId}-refund`}
                      value={option.type}
                      checked={refundChoice === option.type}
                      onChange={() => setRefundChoice(option.type)}
                      aria-invalid={validationAttempted && needsRefundChoice}
                      aria-describedby={validationAttempted && needsRefundChoice ? `${formId}-refund-error` : undefined}
                      className="mt-1 size-4 shrink-0"
                    />
                    <span className="space-y-1 text-sm">
                      <span className="block font-medium">{option.type === "points" ? "Refund as points" : "Original payment method"}</span>
                      <span className="block text-xs text-muted-foreground">{option.description}</span>
                      <span className="block text-xs tabular-nums">Gross {formatCurrency(option.amount)} · Fee {formatCurrency(option.fee)} · Net {formatCurrency(option.netAmount)}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {analysis.resolutionMode === "stripe_pending_resolution"
                  ? "A payment is still processing. Cancelling will stop it where possible; any payment that settles will be handled through the refund process."
                  : "No completed provider payment needs a refund. Cancelling will restore inventory and close the order."}
              </p>
            )}
            {validationAttempted && needsRefundChoice ? <p id={`${formId}-refund-error`} className="text-sm text-destructive">Choose how to refund the paid amount.</p> : null}
          </fieldset>
          <div className="space-y-2 rounded-xl border p-4">
            <p className="text-sm text-muted-foreground">The hold pauses order fulfillment. Cancel any carrier booking separately before confirming below.</p>
            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <input
                ref={carrierRef}
                type="checkbox"
                checked={carrierConfirmed}
                disabled={isSubmitting}
                onChange={(event) => setCarrierConfirmed(event.target.checked)}
                aria-invalid={validationAttempted && !carrierConfirmed}
                aria-describedby={validationAttempted && !carrierConfirmed ? `${formId}-carrier-error` : undefined}
                className="mt-1 size-4 shrink-0"
              />
              <span>Any carrier pickup/shipment has been cancelled or was never booked.</span>
            </label>
            {validationAttempted && !carrierConfirmed ? <p id={`${formId}-carrier-error`} className="text-sm text-destructive">Confirm that any carrier booking has been cancelled or was never made.</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${formId}-notes`}>Cancellation notes (optional)</Label>
            <Textarea id={`${formId}-notes`} value={notes} disabled={isSubmitting} onChange={(event) => setNotes(event.target.value)} placeholder="Example: Customer requested cancellation; carrier pickup cancelled." />
          </div>
        </>
      ) : null}
      {submitError ? <p role="alert" className="text-sm text-destructive">{submitError}</p> : null}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={isSubmitting} onClick={onClose}>Keep on hold</Button>
        <Button type="submit" variant="destructive" disabled={isSubmitting || !canCancel} aria-busy={isSubmitting}>
          {isSubmitting ? <Loader2 aria-hidden="true" className="mr-2 size-4 motion-safe:animate-spin" /> : null}
          {isSubmitting ? "Cancelling..." : "Cancel Order"}
        </Button>
      </DialogFooter>
    </form>
  );
}
